// dependencies
import React from "react";
import { useIntl } from "react-intl";

// components
import Text from "@cloudspire/legacy-resources/src/components/Text2";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import { FieldError } from "@cloudspire/legacy-resources/src/components/Field";
import {
  NumberOfAdults,
  NumberOfChildren,
  NumberOfBabies,
  ChildrenAge,
  NumberOfDogs,
  NumberOfCats,
  NumberOfOtherPets,
} from "@cloudspire/legacy-resources/src/components/TripComposition";

// constants
import { MAX_PETS_LIMIT } from "@cloudspire/legacy-resources/src/constants/booking";

// libraries
import {
  get,
  getDifferentInList,
} from "@cloudspire/legacy-shared/src/libraries";

function normalizeNumber(numberOfPets) {
  const pets = parseInt(numberOfPets);

  if (isNaN(pets)) {
    return 0;
  }

  return pets;
}

type PropsType = {
  parameterList: {
    [name: string]: any;
  };
  errorList: {
    [name: string]: string[];
  };
  onChange;
  room: ReturnType<
    typeof import("@cloudspire/legacy-resources/src/normalizers/room/parseData").default
  >;
};

const TripCompositionContainer: React.FunctionComponent<PropsType> = React.memo(
  function TripCompositionContainer(props) {
    const { parameterList, room, errorList, onChange } = props;

    const intl = useIntl();

    const hasErrorOnNumberOfAdults = get(errorList, "adults", []).length > 0;
    const hasErrorOnNumberOfChildren = get(errorList, "childs", []).length > 0;
    const hasErrorOnNumberOfBabies = get(errorList, "babies", []).length > 0;
    const hasErrorOnNumberOfPets = get(errorList, "pets", []).length > 0;
    const hasErrorOnNumberOfDogs = get(errorList, "dogs", []).length > 0;
    const hasErrorOnNumberOfCats = get(errorList, "cats", []).length > 0;

    const dogs = normalizeNumber(parameterList?.dogs);
    const cats = normalizeNumber(parameterList?.cats);
    const otherPets = normalizeNumber(parameterList?.other_pets);

    const pets = dogs + cats + otherPets;

    return (
      <>
        <Text>
          {intl.formatMessage({
            defaultMessage: "Le voyage sera composé de ",
          })}
          <NumberOfAdults
            name="adults"
            required={true}
            value={parameterList?.adults ?? ""}
            max={room.maxCapacity}
            min={1}
            onChange={onChange}
          />
          {", "}
          <NumberOfChildren
            name="childs"
            value={parameterList?.childs ?? ""}
            max={room.maxCapacity}
            min={0}
            data-gtm="booking-children"
            onChange={onChange}
          />{" "}
          {", "}
          <NumberOfBabies
            name="babies"
            value={parameterList?.babies ?? ""}
            min={0}
            data-gtm="booking-babies"
            onChange={onChange}
          />
          <br />
          {(Number(parameterList?.childs ?? "") || 0) > 0 && (
            <>
              <ChildrenAge
                name="birthyears"
                numberOfChildren={Number(parameterList?.childs ?? "") || 0}
                value={parameterList?.birthyears ?? []}
                onChange={onChange}
              />
            </>
          )}
          {room.arePetsAllowed && (
            <div>
              <NumberOfDogs
                name="dogs"
                value={parameterList?.dogs ?? ""}
                min={0}
                max={MAX_PETS_LIMIT - pets + dogs}
                data-gtm="booking-dogs"
                onChange={onChange}
              />
              {", "}
              <NumberOfCats
                name="cats"
                value={parameterList?.cats ?? ""}
                min={0}
                max={MAX_PETS_LIMIT - pets + cats}
                data-gtm="booking-cats"
                onChange={onChange}
              />
              {intl.formatMessage({
                defaultMessage: " et ",
              })}
              <NumberOfOtherPets
                name="other_pets"
                value={parameterList?.other_pets ?? ""}
                min={0}
                max={MAX_PETS_LIMIT - pets + otherPets}
                data-gtm="booking-other-pets"
                onChange={onChange}
              />
              {intl.formatMessage(
                {
                  defaultMessage:
                    " {numberOfPets, plural, =0 {sera également présent} one {sera également présent} other {seront également présents}}.",
                },
                {
                  numberOfPets: pets,
                }
              )}
            </div>
          )}
        </Text>
        {(hasErrorOnNumberOfAdults ||
          hasErrorOnNumberOfChildren ||
          hasErrorOnNumberOfBabies ||
          hasErrorOnNumberOfPets ||
          hasErrorOnNumberOfDogs ||
          hasErrorOnNumberOfCats) && (
          <>
            <Stack marginTop={0.5} />

            {hasErrorOnNumberOfAdults && (
              <>
                <Stack marginTop={0.5} />

                <FieldError errorList={get(errorList, "adults", [])}>
                  {get(errorList, "adults", []).join("\n")}
                </FieldError>
              </>
            )}

            {hasErrorOnNumberOfChildren && (
              <>
                <Stack marginTop={0.5} />

                <FieldError errorList={get(errorList, "childs", [])}>
                  {get(errorList, "childs", []).join("\n")}
                </FieldError>
              </>
            )}

            {hasErrorOnNumberOfBabies && (
              <>
                <Stack marginTop={0.5} />

                <FieldError errorList={get(errorList, "babies", [])}>
                  {get(errorList, "babies", []).join("\n")}
                </FieldError>
              </>
            )}

            {hasErrorOnNumberOfPets && (
              <>
                <Stack marginTop={0.5} />

                <FieldError errorList={get(errorList, "pets", [])}>
                  {get(errorList, "pets", []).join("\n")}
                </FieldError>
              </>
            )}

            {hasErrorOnNumberOfDogs && (
              <>
                <Stack marginTop={0.5} />

                <FieldError errorList={get(errorList, "dogs", [])}>
                  {get(errorList, "dogs", []).join("\n")}
                </FieldError>
              </>
            )}

            {hasErrorOnNumberOfCats && (
              <>
                <Stack marginTop={0.5} />

                <FieldError errorList={get(errorList, "cats", [])}>
                  {get(errorList, "cats", []).join("\n")}
                </FieldError>
              </>
            )}
          </>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.children !== nextProps.children) {
      return false;
    }

    if (prevProps.onChange !== nextProps.onChange) {
      return false;
    }

    if (
      getDifferentInList(
        [
          "numberOfAdults",
          "numberOfChildren",
          "numberOfBabies",
          "childrenAge",
          "numberOfPets",
          "numberOfDogs",
          "numberOfCats",
        ],
        prevProps.parameterList,
        nextProps.parameterList
      )
    ) {
      return false;
    }

    if (
      getDifferentInList(
        ["adults", "childs", "babies", "pets", "dogs", "cats"],
        prevProps.errorList,
        nextProps.errorList
      )
    ) {
      return false;
    }

    return true;
  }
);

export default TripCompositionContainer;
